import { useEffect, useState } from "react";
import AuiFileUpload from "../../../components/AuiDropdown/AuiFIleUpload";
import {
  downloadUploadedDocument,
  getDocumentList,
  sessionStatusUpdate,
  uploadDocument,
} from "../../../services/training-session.service";
import { useLocation, useNavigate } from "react-router-dom";
import Headings from "../../../components/Headings/Headings";
import AuiButton from "../../../components/Buttons/Buttons";
import "./Upload.scss";
import Docs from "../../../assets/images/docs.png";
import { useDispatch } from "react-redux";
import { showAlert } from "../../../store/alertSlice";
import AuiIcon from "../../../components/AuiDropdown/AuiIcon";

const Upload = ({ trainingSession }: { trainingSession: any }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = parseInt(queryParams.get("id") ?? "-1");
  const [isDisable, setIsDisable] = useState(true);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isConceptDownloadLoading, setIsConceptDownloadLoading] =
    useState(false);
  const [isSaveBtnLoading, setIsSaveBtnLoading] = useState(false);
  const [isDisableSave, setIsDisableSave] = useState(true);
  const [fileDetails, setFileDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(sessionId !== -1);
  const [isPageLoading, setIsPageLoading] = useState(sessionId !== -1);
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (sessionId === -1) {
      return;
    }
    const fetchData = async () => {
      setIsLoading(true);
      const response = await getDocumentList(sessionId, "Concept Note");
      setIsLoading(false);
      if (response?.data?.trainingInstances.length) {
        const file = response.data.trainingInstances.reverse()?.[0];
        setFileDetails(file);
        if (trainingSession) {
          if (trainingSession?.status === "DRAFT" || trainingSession?.status === "REJECTED") {
            setIsDisableSave(false);
          }
          setIsDisable(false);
        }
      }
    };
    fetchData();
  }, [sessionId, trainingSession]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const deleteConceptNote = () => {
    setFileDetails(null);
    setIsDisable(true);
    setIsDisableSave(true);
  };

  const handleFileChange = async (files: FileList | null) => {
    if (files && sessionId !== -1) {
      setIsUploading(true);
      const fileToUpload: any = files[0];
      const uploadResponse = await uploadDocument(
        fileToUpload,
        "Concept Note",
        sessionId
      );
      if (
        uploadResponse.status === 200 &&
        uploadResponse.data?.trainingDocument?.status === "COMPLETED"
      ) {
        await getDocuments();
      } else {
        setIsDisable(false);
      }
      setIsUploading(false);
    } else {
      setIsDisable(true);
    }
  };

  const getDocuments = async () => {
    const documentListResponse = await getDocumentList(
      sessionId,
      "Concept Note"
    );
    if (
      documentListResponse.status === 200 &&
      documentListResponse?.data?.trainingInstances.length >= 0
    ) {
      const file = documentListResponse.data.trainingInstances.reverse()?.[0];
      setFileDetails(file);
      if (
        trainingSession?.status === "DRAFT" ||
        trainingSession?.status === "REJECTED"
      ) {
        setIsDisable(false);
        setIsDisableSave(false);
      } else if (trainingSession?.status === "PENDING") {
        setIsDisable(false);
        setIsDisableSave(true);
      } else {
        setIsDisable(false);
      }
    } else {
      setIsDisable(false);
    }
  }

  const getConceptNoteTemplate = async () => {
    if (!sessionId) return;
    await downloadUploadedDocument(
      sessionId,
      0,
      0,
      "CONCEPT NOTE"
    )
  }

  const handleSendRequest = async () => {
    setIsBtnLoading(true);
    if (isDisable) {
      return;
    }
    const response = await sessionStatusUpdate(sessionId);
    setIsBtnLoading(false);
    if(response.status === 200) {
      dispatch(showAlert({ message: "Request sent successfully!", type: "success"}));
    }
    navigate("/course-listing");
  };

  const handleSubmit = () => {
    setIsSaveBtnLoading(true);
    if (isDisable || isSaveBtnLoading) {
      return;
    }
    setIsSaveBtnLoading(false);
    navigate(`/create-course/studentDetail?id=${sessionId}`);
  };

  const handleUploadBackBtn = () => {
    navigate(`/create-course/classDetails?id=${sessionId}`);
  };

  const handleDownload = async () => {
    if (isConceptDownloadLoading) return;
    setIsConceptDownloadLoading(true);
    try {
      const documentListResponse = await getDocumentList(sessionId, "Concept Note");
      if (documentListResponse?.data?.trainingInstances.length) {
        const latestDocument = documentListResponse.data.trainingInstances.reverse()?.[0];
        if (latestDocument?.id) {
          const documentId = latestDocument.id;
          await downloadUploadedDocument(sessionId, documentId);
        } else {
          console.error("No document found for download.");
        }
      }
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsConceptDownloadLoading(false);
    }
  };

  let uploadSection;
  if (isLoading || isUploading) {
    uploadSection = (
      <div className="aui-loader">
        <aui-loader type="content"></aui-loader>
      </div>
    );
  } else if (!fileDetails) {
        uploadSection = (
          <div className="pb-4 pb-md-5 uploadFileSection">
            <AuiFileUpload
              allowedfiletypes=".pdf, .docx"
              maxuploadsize={1050000}
              iseditable={false}
              fileuploadlabel={true}
              auifiluploaddirection="column"
              auifiluploaderrorstate={false}
              auifiluploadrequired={true}
              auifiluploadrequiredmessage="Upload files should be mandatory"
              auifileuploaderrormessage="Please upload the correct format"
              onChange={handleFileChange}
              data-testid="fileUpload"
            />
            <div className="downloadTemplate">
                <div className="my-3 ml-3">
                    <img src={Docs} alt="" />
                    <AuiButton
                       variant="read-more"
                       size="medium"
                       buttontitle="Download concept note template"
                       arialabel="Download Concept Note Template"
                       buttonid="downloadTemplateId"
                       buttonclass="ml-2 font-600"
                       buttonClick={getConceptNoteTemplate}
                     />
                </div>
            </div>
          </div>
        );
      } else {
        uploadSection = (
          <div>
            <span>
              {fileDetails?.filepath}
              <button className="ml-2" onClick={deleteConceptNote}>
                <aui-icon
                  svgclass="aui-icon-cross"
                  pathclass="aui-path-cross"
                  icon="closeIcon"
                  svgwidth="10"
                  svgheight="10"
                  aria-label="Delete file"
                />
              </button>
            </span>
            {fileDetails && (
              <div className="pt-3 pb-5">
                {isConceptDownloadLoading ? (
                  <aui-button
                    buttontitle="Download"
                    variant="secondary"
                    loading
                    size="medium"
                    buttonclass="custBtn py-2"
                  />
                ) : (
                  <button className="custBtn py-2" onClick={handleDownload}>
                    <AuiIcon iconClass="download" />
                    <span className="ml-2">Download</span>
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="Upload-container">
          {isPageLoading ? (
            <div className="aui-loader">
              <aui-loader type="content"></aui-loader>
            </div>
          ) : (
            <>
              <Headings
                className={`headings blue mb-3`}
                defaultClass="h7"
                size="h2"
                id="classInfodetails"
              >
                Concept Note
              </Headings>
              {uploadSection}
        <div className="d-flex flex-column-reverse flex-sm-row justify-content-between align-items-center pt-3 pt-md-5 border-top">
          <div className="backArrowBtn mt-2 mt-sm-0">
            <button
              className="btn general-text-link m-0"
              onClick={handleUploadBackBtn}
            >
              <aui-icon icon="arrowleft" svgwidth="20" svgheight="20" />
              Back
            </button>
          </div>
          <div className="d-flex justify-content-end">
            <div className="uploadSendRequestBtn">
              {(trainingSession?.status === "DRAFT" || trainingSession?.status === "REJECTED") && (
                <AuiButton
                  variant="secondary"
                  buttontitle="Send Request"
                  size="medium"
                  buttonid=""
                  buttonClick={handleSendRequest}
                  disabled={isDisableSave}
                  loading={isBtnLoading}
                />
              )}
            </div>
            <span className="ml-3">
              <AuiButton
                variant="primary"
                buttontitle="Save & Next"
                size="medium"
                buttonClick={handleSubmit}
                disabled={isDisable}
                buttonid=""
                loading={isSaveBtnLoading}
              />
            </span>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default Upload;
